import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { ReactNode, Ref } from 'react';

import { IconWrapper } from './IconWrapper';

import AlertTriangle from '../icons/AlertTriangle.svg?react';
import CheckCircle from '../icons/CheckCircle.svg?react';
import InformationCircle from '../icons/InformationCircle.svg?react';

export type SnackbarVariant = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarProps {
  readonly id: string | number;
  readonly variant: SnackbarVariant;
  readonly message: string;
  readonly onClose?: (id: string | number) => void;
  readonly action?: ReactNode;
  readonly loading?: boolean;
  readonly ref?: Ref<HTMLDivElement>;
}

function SnackbarIcon({ variant, loading }: Pick<SnackbarProps, 'variant' | 'loading'>) {
  if (loading) return <CircularProgress size={16} color="inherit" />;
  if (variant === 'success') return <CheckCircle />;
  if (variant === 'info') return <InformationCircle />;
  return <AlertTriangle />;
}

export function Snackbar({ id, variant, message, onClose, action, loading, ref }: SnackbarProps) {
  return (
    <Alert
      ref={ref}
      severity={variant}
      variant="filled"
      icon={
        <IconWrapper size="s">
          <SnackbarIcon variant={variant} loading={loading} />
        </IconWrapper>
      }
      onClose={onClose ? () => onClose(id) : undefined}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
          {message}
        </Typography>
        {action ? <Box sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{action}</Box> : null}
      </Stack>
    </Alert>
  );
}
